// src/theme/surfaces.ts
//
// The surface treatment: how a panel meets the page.
//
// The app's geometry was inherited from macOS Ventura/Sonoma — glassmorphism at
// 20–44px blur, 8–16px radii, and a 25-step shadow ramp reaching 60% black.
// That reads as a consumer desktop app. Three candidates were built and
// compared on real screens (glass / hybrid / flat); `hybrid` was chosen and the
// other two removed.
//
// Hybrid keeps frost on the CHROME — header, drawer, command bar, menus — where
// translucency means "floating above the content", and makes everything else
// opaque: a 1px divider edge, a 6px radius, and a flat shadow. Content that
// stops floating is what separates a research tool from a consumer app; chrome
// that still floats is what stops it feeling like a spreadsheet.

import type { Shadows } from '@mui/material/styles';
import type { CSSObject } from '@mui/material/styles';
import { neutral } from './brand';

export type Mode = 'light' | 'dark';

export interface SurfaceRadius {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  pill: number;
}

export interface SurfacePreset {
  radius: SurfaceRadius;
  /**
   * Ambient chrome: header, drawer, bottom nav. Translucent on purpose — you
   * want to sense the content moving underneath as it scrolls.
   */
  chrome: (mode: Mode) => CSSObject;
  /**
   * Content-bearing overlays: menus and dialogs. Nearly opaque, because the
   * page behind them is not ambience — it is text, and at the chrome's 0.82 it
   * showed through and competed with the menu's own labels. Verified in the
   * browser, not assumed.
   */
  overlay: (mode: Mode) => CSSObject;
  /** A surface nested inside chrome — e.g. the chat input inside the panel. */
  panel: (mode: Mode) => CSSObject;
  shadows: Shadows;
  borderWidth: number;
  /** The modal overlay behind a dialog. */
  scrim: (mode: Mode) => CSSObject;
  /** Whether modals scale-in on open, and whether raised controls carry a gloss. */
  bloom: boolean;
}

// The active preset rides on the theme object, so any `sx` callback can reach
// it without a hook and without threading props. This is the mechanism that
// keeps radius and surface treatment consistent across every component.
declare module '@mui/material/styles' {
  interface Theme {
    surface: SurfacePreset;
  }
  interface ThemeOptions {
    surface?: SurfacePreset;
  }
}

/**
 * A 25-step shadow ramp. MUI indexes `elevation` straight into this array, so
 * it must be exactly 25 long. `peak` caps the alpha of the last step; offset
 * and blur grow with it.
 */
function shadowRamp(peak: number): Shadows {
  const steps = Array.from({ length: 24 }, (_, i) => {
    const t = (i + 1) / 24;
    const alpha = +(peak * (0.18 + 0.82 * t)).toFixed(3);
    const y = Math.round(1 + t * 19);
    const blur = Math.round(2 + t * 58);
    return `0 ${y}px ${blur}px rgba(0,0,0,${alpha})`;
  });
  return ['none', ...steps] as unknown as Shadows;
}

/**
 * Frost, expressed as a `@supports` pair: the opaque fallback lands first so
 * browsers without backdrop-filter get a solid panel instead of see-through
 * chaos, then the blur overrides it where it is actually supported.
 */
function frosted(bg: string, fallbackBg: string, border: string, blur: string, shadow: string): CSSObject {
  return {
    backgroundColor: fallbackBg,
    backgroundImage: 'none',
    border: `1px solid ${border}`,
    boxShadow: shadow,
    '@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
      backgroundColor: bg,
      backdropFilter: blur,
      WebkitBackdropFilter: blur,
    },
  };
}

/** An opaque surface: ground, hairline edge, and whatever lift the preset allows. */
function solid(bg: string, border: string, shadow: string): CSSObject {
  return {
    backgroundColor: bg,
    backgroundImage: 'none',
    border: `1px solid ${border}`,
    boxShadow: shadow,
  };
}

export const surface: SurfacePreset = {
  // TWO TIERS DO THE WORK, and they resolve a contradiction that sat between
  // two files: createInternalTheme declared `RADIUS = 4` ("instrument, not
  // product surface") while this file's own header specified "a 6px radius".
  // Both were right about different things — a control wants 4, a panel wants
  // 6 — so `sm` is the control step and `md` is the panel step, and neither
  // file hardcodes a number any more.
  radius: { xs: 2, sm: 4, md: 6, lg: 8, xl: 12, pill: 999 },
  borderWidth: 1,
  shadows: shadowRamp(0.24),
  chrome: (mode) =>
    mode === 'dark'
      ? frosted(
          'rgba(21, 21, 28, 0.82)', neutral[900],
          neutral[800], 'blur(12px) saturate(140%)',
          '0 1px 3px rgba(0, 0, 0, 0.30)',
        )
      : frosted(
          'rgba(255, 255, 255, 0.85)', neutral[0],
          neutral[200], 'blur(12px) saturate(140%)',
          '0 1px 3px rgba(0, 0, 0, 0.06)',
        ),
  overlay: (mode) =>
    mode === 'dark'
      ? frosted(
          'rgba(21, 21, 28, 0.97)', neutral[900],
          neutral[800], 'blur(12px) saturate(140%)',
          '0 8px 24px rgba(0, 0, 0, 0.44)',
        )
      : frosted(
          'rgba(255, 255, 255, 0.97)', neutral[0],
          neutral[200], 'blur(12px) saturate(140%)',
          '0 8px 24px rgba(0, 0, 0, 0.12)',
        ),
  panel: (mode) =>
    mode === 'dark'
      ? solid(neutral[850], neutral[800], 'none')
      : solid(neutral[100], neutral[200], 'none'),
  scrim: (mode) => ({
    backgroundColor: mode === 'dark' ? 'rgba(4, 4, 8, 0.58)' : 'rgba(21, 21, 28, 0.34)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  }),
  bloom: true,
};
